import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CloudDownload, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const DEFAULT_SOURCE = 'https://casa-samba-music.preview.emergentagent.com';

/**
 * Admin-only one-click sync of content from a source environment (preview)
 * into THIS environment (live). Only inserts missing documents — never
 * updates or deletes. Users, orders, payments, class registrations and all
 * other user-generated collections are hard-coded on the server as denied.
 */
const AdminContentSync = () => {
  const [sourceUrl, setSourceUrl] = useState(DEFAULT_SOURCE);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const run = async () => {
    setRunning(true);
    setReport(null);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        // No token at all — bounce to admin login and come straight back.
        window.location.href = `/admin?returnTo=${encodeURIComponent('/admin/content-sync')}`;
        return;
      }
      const url = `${API_URL}/api/admin/sync/pull?source_url=${encodeURIComponent(sourceUrl)}&verify_media=true`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 || (data?.detail || '').toLowerCase().includes('expired')) {
        // Session expired — clear the bad token and bounce to login with a
        // return URL so the admin lands right back here after signing in.
        localStorage.removeItem('auth_token');
        window.location.href = `/admin?returnTo=${encodeURIComponent('/admin/content-sync')}&reason=expired`;
        return;
      }
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setReport(data);
    } catch (e) {
      setError(e.message || 'Sync failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-4" data-testid="back-to-dash">
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <CloudDownload className="w-7 h-7 text-yellow-400" />
          <h1 className="text-3xl font-black">Sync preview content to live</h1>
        </div>
        <p className="text-sm text-zinc-400 mb-6 max-w-2xl">
          Copies any content that exists on <code className="text-yellow-400">source</code> but is missing here. Keeps the same IDs.
          Never changes or deletes anything. Users, orders, payments, bookings and class registrations are hard-blocked on the server and will never be touched.
        </p>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-5">
          <label className="block text-sm font-semibold mb-2">Source URL (preview)</label>
          <input
            type="url"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            disabled={running}
            data-testid="sync-source-url"
            className="w-full px-4 py-3 bg-black border border-zinc-700 rounded-lg text-white focus:border-yellow-500 outline-none"
            placeholder="https://..."
          />
          <button
            onClick={run}
            disabled={running || !sourceUrl}
            data-testid="sync-run-btn"
            className="mt-4 w-full py-3 rounded-full font-bold bg-yellow-400 text-black hover:bg-yellow-300 disabled:bg-zinc-700 disabled:text-zinc-400 flex items-center justify-center gap-2"
          >
            {running ? <><Loader2 className="w-5 h-5 animate-spin" /> Syncing — this can take a minute...</> : <>Pull missing content from preview</>}
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-4 flex gap-3 mb-5" data-testid="sync-error">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <div className="text-red-300 text-sm">{error}</div>
          </div>
        )}

        {report && (
          <div className="space-y-5">
            <div className="bg-green-500/10 border border-green-500/40 rounded-xl p-5" data-testid="sync-summary">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
                <h2 className="text-xl font-black text-green-300">
                  {report.total_added === 0 ? 'Already in sync' : `Added ${report.total_added} new item${report.total_added === 1 ? '' : 's'}`}
                </h2>
              </div>
              <div className="text-sm text-zinc-300">
                Pulled from <code className="text-yellow-300">{report.source_url}</code>
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-5 py-3 bg-zinc-800/60 font-bold text-sm">Per-collection breakdown</div>
              <div className="divide-y divide-zinc-800">
                {Object.entries(report.collections)
                  .sort((a, b) => (b[1].added || 0) - (a[1].added || 0))
                  .map(([name, info]) => (
                    <div key={name} className="px-5 py-3 flex items-center justify-between" data-testid={`sync-col-${name}`}>
                      <div className="text-sm">
                        <div className="font-medium">{name}</div>
                        {info.error && <div className="text-xs text-red-400 mt-1">{info.error}</div>}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-zinc-500">had: {info.existing ?? 0}</span>
                        <span className={`font-bold ${(info.added || 0) > 0 ? 'text-green-400' : 'text-zinc-500'}`}>
                          + {info.added || 0}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {report.missing_files && report.missing_files.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-5" data-testid="sync-missing-files">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                  <h2 className="text-lg font-black text-amber-200">
                    {report.missing_files.length} referenced file{report.missing_files.length === 1 ? '' : 's'} not found on this server
                  </h2>
                </div>
                <p className="text-sm text-zinc-300 mb-3">
                  New database records were added, but these audio or image files they reference don't exist on live yet. Upload them, or re-upload the parent item from the admin UI.
                </p>
                <div className="max-h-80 overflow-y-auto text-xs font-mono bg-black/50 rounded-lg p-3 space-y-1">
                  {report.missing_files.map((m, i) => (
                    <div key={i} className="text-amber-300">
                      <span className="text-zinc-500">[{m.collection}]</span> {m.url}
                      <span className="text-zinc-600 ml-2">(HTTP {m.status})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContentSync;
